import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { transparenciaService } from '../services/transparenciaService';
import { emendaNumeroExercicio } from '../utils/emendaFormatters';
import DashboardPagination from '../components/DashboardPagination';
import { PortalAcessibilidadeShell, SituacaoBadge, usePortalAcessibilidade } from '../components/PortalAcessibilidade';
import { formatCurrency, usePortalTitle } from '../utils/transparenciaFormat';

const POR_PAGINA = 20;

const TransparenciaParlamentarPage: React.FC = () => {
  const { tema, barra } = usePortalAcessibilidade();
  const { slug = '' } = useParams<{ slug: string }>();
  const [exercicioEscolhido, setExercicioEscolhido] = useState<number | null>(null);
  const [textoInstituicao, setTextoInstituicao] = useState('');
  const [instituicaoAtiva, setInstituicaoAtiva] = useState('');
  const [pagina, setPagina] = useState(1);

  const perfilQuery = useQuery({
    queryKey: ['transparencia-parlamentar', slug],
    queryFn: () => transparenciaService.parlamentar(slug),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const perfil = perfilQuery.data;
  usePortalTitle(perfil?.nome ?? 'Parlamentar');

  // Exercício mais recente é o padrão; o parlamentar pode ter emendas só em anos antigos.
  const exercicio = exercicioEscolhido ?? perfil?.exercicios[0]?.exercicio ?? null;
  const resumo = useMemo(
    () => perfil?.exercicios.find(item => item.exercicio === exercicio),
    [perfil, exercicio],
  );

  // Aplica o texto digitado depois de uma pausa, para não buscar a cada tecla.
  useEffect(() => {
    const timer = setTimeout(() => {
      setInstituicaoAtiva(textoInstituicao.trim());
      setPagina(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [textoInstituicao]);

  const filtros = { parlamentar: slug, exercicio: exercicio ?? undefined, instituicao: instituicaoAtiva || undefined };

  const totaisQuery = useQuery({
    queryKey: ['transparencia-parlamentar-totais', slug, exercicio, instituicaoAtiva],
    queryFn: () => transparenciaService.totais(filtros),
    enabled: !!perfil && exercicio != null && !!instituicaoAtiva,
    staleTime: 5 * 60 * 1000,
  });

  const emendasQuery = useQuery({
    queryKey: ['transparencia-parlamentar-emendas', slug, exercicio, instituicaoAtiva, pagina],
    queryFn: () => transparenciaService.list(filtros, pagina - 1, POR_PAGINA),
    enabled: !!perfil && exercicio != null,
    staleTime: 5 * 60 * 1000,
  });
  const emendas = emendasQuery.data?.items ?? [];

  const escolherExercicio = (ano: number) => {
    setExercicioEscolhido(ano);
    setPagina(1);
  };

  // Com instituição digitada, os quadros vêm do recorte filtrado; sem ela, do resumo do exercício.
  const quadro = instituicaoAtiva
    ? totaisQuery.data && {
        totalEmendas: totaisQuery.data.totalItems,
        previsto: totaisQuery.data.previsto,
        empenhado: totaisQuery.data.empenhado,
        pago: totaisQuery.data.pago,
      }
    : resumo && {
        totalEmendas: resumo.totalEmendas,
        previsto: resumo.previsto,
        empenhado: resumo.empenhado,
        pago: resumo.pago,
      };

  return (
    <PortalAcessibilidadeShell tema={tema} barra={barra}>
      <header className={tema.header}>
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-5 sm:px-6 lg:px-8">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${tema.headerKicker}`}>Portal da Transparência</p>
            <h1 className="mt-1 text-2xl font-bold">{perfil?.nome ?? 'Parlamentar'}</h1>
            {perfil && (
              <p className={`mt-1 text-sm ${tema.headerText}`}>
                {[perfil.bancada ? 'Bancada' : null, perfil.partido, perfil.esfera].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
          <Link to="/transparencia/parlamentares" className={`rounded-md border px-4 py-2 text-sm font-semibold ${tema.headerButton}`}>
            Outros parlamentares
          </Link>
        </div>
      </header>

      <main id="conteudo-principal" className="mx-auto w-full max-w-7xl flex-1 space-y-5 px-4 py-6 sm:px-6 lg:px-8">
        {perfilQuery.isLoading && <p className="py-16 text-center text-sm">Carregando dados...</p>}
        {perfilQuery.isError && (
          <div className="py-16 text-center">
            <p className="font-semibold">Parlamentar não encontrado.</p>
            <Link to="/transparencia/parlamentares" className={`mt-2 inline-block text-sm font-semibold underline ${tema.link}`}>Ver a lista de parlamentares</Link>
          </div>
        )}

        {perfil?.dadosPublicos && (
          <section className={`flex flex-col gap-5 rounded-xl p-5 sm:flex-row ${tema.card}`} aria-label="Sobre o parlamentar">
            {perfil.dadosPublicos.temFoto && (
              <img
                src={transparenciaService.fotoParlamentarUrl(slug, perfil.dadosPublicos.fotoVersao)}
                alt={`Foto de ${perfil.nome}`}
                className="h-32 w-32 shrink-0 rounded-xl object-cover"
              />
            )}
            <div className="min-w-0 flex-1 space-y-3">
              {perfil.dadosPublicos.biografia && (
                <p className="whitespace-pre-line text-sm leading-6">{perfil.dadosPublicos.biografia}</p>
              )}
              <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                {perfil.dadosPublicos.contatoEmail && (
                  <div><dt className={`text-xs font-semibold uppercase tracking-wide ${tema.muted}`}>E-mail</dt>
                    <dd><a href={`mailto:${perfil.dadosPublicos.contatoEmail}`} className={`underline ${tema.link}`}>{perfil.dadosPublicos.contatoEmail}</a></dd></div>
                )}
                {perfil.dadosPublicos.contatoTelefone && (
                  <div><dt className={`text-xs font-semibold uppercase tracking-wide ${tema.muted}`}>Telefone</dt>
                    <dd>{perfil.dadosPublicos.contatoTelefone}</dd></div>
                )}
                {perfil.dadosPublicos.contatoEndereco && (
                  <div className="sm:col-span-2"><dt className={`text-xs font-semibold uppercase tracking-wide ${tema.muted}`}>Gabinete</dt>
                    <dd>{perfil.dadosPublicos.contatoEndereco}</dd></div>
                )}
              </dl>
              {[['Site', perfil.dadosPublicos.site], ['Instagram', perfil.dadosPublicos.instagram], ['Facebook', perfil.dadosPublicos.facebook]]
                .filter(([, url]) => !!url).length > 0 && (
                <p className="flex flex-wrap gap-x-5 gap-y-1 text-sm font-semibold">
                  {([['Site', perfil.dadosPublicos.site], ['Instagram', perfil.dadosPublicos.instagram], ['Facebook', perfil.dadosPublicos.facebook]] as const)
                    .filter(([, url]) => !!url)
                    .map(([rotulo, url]) => (
                      <a key={rotulo} href={url as string} target="_blank" rel="noopener noreferrer" className={`underline ${tema.link}`}>{rotulo}</a>
                    ))}
                </p>
              )}
            </div>
          </section>
        )}

        {perfil && perfil.exercicios.length === 0 && (
          <p className="py-12 text-center font-semibold">Este parlamentar ainda não tem emendas publicadas.</p>
        )}

        {perfil && perfil.exercicios.length > 0 && (
          <>
            <section aria-label="Exercício" className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold">Exercício:</span>
              {perfil.exercicios.map(item => (
                <button
                  key={item.exercicio}
                  type="button"
                  onClick={() => item.exercicio != null && escolherExercicio(item.exercicio)}
                  aria-pressed={item.exercicio === exercicio}
                  className={`rounded-md border px-3 py-1.5 text-sm font-semibold ${item.exercicio === exercicio ? tema.primary : tema.secondary}`}
                >
                  {item.exercicio} <span className="font-normal opacity-80">({item.totalEmendas})</span>
                </button>
              ))}
              <label className="relative ml-0 flex items-center gap-2 text-sm font-semibold sm:ml-4">
                Instituição:
                <span className="relative">
                  <MagnifyingGlassIcon className={`pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 ${tema.muted}`} />
                  <input
                    type="text"
                    value={textoInstituicao}
                    onChange={e => setTextoInstituicao(e.target.value)}
                    placeholder="Digite o nome da instituição"
                    className={`w-64 max-w-full pl-8 pr-8 ${tema.input}`}
                  />
                  {textoInstituicao && (
                    <button
                      type="button"
                      onClick={() => setTextoInstituicao('')}
                      aria-label="Limpar filtro de instituição"
                      className={`absolute right-2 top-1/2 -translate-y-1/2 ${tema.muted}`}
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </span>
              </label>
            </section>

            {resumo && quadro && (
              <section className={`overflow-hidden rounded-xl ${tema.card}`} aria-label={`Resumo de ${exercicio}`}>
                <div className={`grid grid-cols-2 divide-x divide-y lg:grid-cols-4 lg:divide-y-0 ${tema.divider}`}>
                  {[
                    ['Emendas', quadro.totalEmendas.toLocaleString('pt-BR')],
                    ['Previsto', formatCurrency(quadro.previsto)],
                    ['Empenhado', formatCurrency(quadro.empenhado)],
                    ['Pago', formatCurrency(quadro.pago)],
                  ].map(([label, value]) => (
                    <div key={label} className="px-4 py-3">
                      <p className={`text-xs font-semibold uppercase tracking-wide ${tema.muted}`}>{label}</p>
                      <p className="mt-1 text-base font-bold">{value}</p>
                    </div>
                  ))}
                </div>
                {!instituicaoAtiva && (
                <div className={`flex flex-wrap gap-2 border-t px-4 py-3 ${tema.divider}`}>
                  {Object.entries(resumo.porStatus).map(([status, quantidade]) => (
                    <span key={status} className={`rounded-full px-3 py-1 text-xs font-semibold ${tema.chip}`}>
                      {status}: {quantidade}
                    </span>
                  ))}
                </div>
                )}
              </section>
            )}

            <section className={`overflow-hidden rounded-xl ${tema.card}`}>
              <div className={`border-b px-5 py-4 ${tema.divider}`}>
                <h2 className="font-semibold">Emendas de {exercicio}{instituicaoAtiva ? ` · ${instituicaoAtiva}` : ''}</h2>
              </div>
              {emendasQuery.isLoading && <p className="py-10 text-center text-sm">Carregando emendas...</p>}
              {emendasQuery.isError && <p className="py-10 text-center text-sm text-red-600 dark:text-red-400">Não foi possível carregar as emendas.</p>}
              {!emendasQuery.isLoading && !emendasQuery.isError && emendas.length === 0 && (
                <p className="py-10 text-center text-sm font-semibold" role="status">{instituicaoAtiva ? `Nenhuma emenda de ${exercicio} para “${instituicaoAtiva}”.` : `Nenhuma emenda publicada em ${exercicio}.`}</p>
              )}
              {emendas.length > 0 && (
                <>
                  <ul className={`divide-y md:hidden ${tema.divider}`}>
                    {emendas.map((emenda, index) => {
                      const identificacao = emendaNumeroExercicio(emenda.numeroEmenda, emenda.exercicio, emenda.officialCode);
                      return (
                        <li key={emenda.id ?? index} className="px-4 py-4">
                          <div className="flex items-start justify-between gap-3">
                            <p className="font-semibold">Emenda {identificacao.numero}/{identificacao.exercicio}</p>
                            <SituacaoBadge status={emenda.status} alto={tema.alto} />
                          </div>
                          <p className={`text-xs ${tema.muted}`}>Para: {emenda.institutionName || 'beneficiário não informado'}</p>
                          <p className="mt-2 line-clamp-2 text-sm">{emenda.objectDetail || 'Objeto não informado'}</p>
                          <p className="mt-2 text-sm"><span className={tema.muted}>Previsto:</span> <span className="font-semibold">{formatCurrency(emenda.value)}</span></p>
                          {emenda.officialCode && (
                            <Link to={`/transparencia/emendas/${encodeURIComponent(emenda.officialCode)}`} className={`mt-2 inline-block text-sm font-semibold underline ${tema.link}`}>
                              Ver a emenda {identificacao.numero}/{identificacao.exercicio}
                            </Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  <div className="hidden overflow-x-auto md:block">
                    <table className={`w-full divide-y text-sm ${tema.divider}`}>
                      <caption className="sr-only">Emendas do parlamentar no exercício</caption>
                      <thead className={tema.tableHead}>
                        <tr>{['Emenda', 'Objeto', 'Beneficiário', 'Valor previsto', 'Situação', ''].map(col => (
                          <th key={col || 'acoes'} scope="col" className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide ${col === 'Valor previsto' ? 'text-right' : 'text-left'}`}>{col || <span className="sr-only">Ações</span>}</th>
                        ))}</tr>
                      </thead>
                      <tbody className={`divide-y ${tema.divider}`}>
                        {emendas.map((emenda, index) => {
                          const identificacao = emendaNumeroExercicio(emenda.numeroEmenda, emenda.exercicio, emenda.officialCode);
                          return (
                            <tr key={emenda.id ?? index} className={tema.rowHover}>
                              <td className="px-4 py-3">
                                <p className="font-semibold">{identificacao.numero}/{identificacao.exercicio}</p>
                                <p className={`text-xs ${tema.muted}`}>{emenda.officialCode || 'Código oficial não informado'}</p>
                              </td>
                              <td className="max-w-xs px-4 py-3"><p className="line-clamp-2">{emenda.objectDetail || 'Não informado'}</p></td>
                              <td className="px-4 py-3">{emenda.institutionName || 'Não informado'}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-right font-medium">{formatCurrency(emenda.value)}</td>
                              <td className="px-4 py-3"><SituacaoBadge status={emenda.status} alto={tema.alto} /></td>
                              <td className="px-4 py-3">
                                {emenda.officialCode && (
                                  <Link to={`/transparencia/emendas/${encodeURIComponent(emenda.officialCode)}`} className={`text-xs font-semibold underline ${tema.link}`}>
                                    Ver emenda<span className="sr-only"> {identificacao.numero}/{identificacao.exercicio}</span>
                                  </Link>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <DashboardPagination
                    page={pagina}
                    pageSize={POR_PAGINA}
                    totalItems={emendasQuery.data?.totalItems ?? 0}
                    onPageChange={setPagina}
                  />
                </>
              )}
            </section>
          </>
        )}
      </main>
    </PortalAcessibilidadeShell>
  );
};

export default TransparenciaParlamentarPage;
